// File: LazyLoadScroll.ts
import type { ComputedRef, Ref } from "vue";
import { computed, nextTick, onBeforeUnmount, watch } from "vue";

export enum ScrollDirection {
  TOP = "top",
  BOTTOM = "bottom",
}

export function useLazyLoadScroll(
  scrollableTarget: Ref<HTMLElement | null>,
  loadMoreHook: (options: object) => Promise<unknown> | void,
  hasNextPage: Ref<boolean> | ComputedRef<boolean>,
  isLoading: Ref<boolean>,
  direction: ScrollDirection = ScrollDirection.BOTTOM,
) {
  const shouldLoadMore = computed(() => {
    if (isLoading.value) {
      return false;
    }
    return hasNextPage.value;
  });

  // Store scroll position values to maintain position when loading at top
  let oldScrollHeight = 0;
  let oldScrollTop = 0;
  let isLoadingMore = false;

  // Watch loading state to restore scroll position after content loads
  watch(isLoading, async (loading) => {
    if (!loading && isLoadingMore && direction === ScrollDirection.TOP && scrollableTarget.value) {
      // Wait for DOM update after loading completes
      await nextTick();
      const target = scrollableTarget.value;
      const newScrollHeight = target.scrollHeight;
      // Adjust scrollTop to maintain relative position with new content
      target.scrollTop = oldScrollTop + (newScrollHeight - oldScrollHeight);
      isLoadingMore = false;
    }
  });

  async function handleScroll(e: Event) {
    if (!e.target) {
      console.error("Cannot handleScroll since the passed event has no target.");
      return;
    }
    const target = e.target as HTMLElement;
    let almostAtEdge = false;

    if (direction === ScrollDirection.BOTTOM) {
      const scrollState = target.scrollTop + target.getBoundingClientRect().height;
      const partialScrollHeight = target.scrollHeight / 3;
      const loadOffset = partialScrollHeight < 200 ? 200 : partialScrollHeight;
      const loadItemsAt = target.scrollHeight - loadOffset;
      almostAtEdge = scrollState >= loadItemsAt;
    } else if (direction === ScrollDirection.TOP) {
      const partialScrollHeight = target.scrollHeight / 3;
      const loadOffset = partialScrollHeight < 200 ? 200 : partialScrollHeight;
      almostAtEdge = target.scrollTop <= loadOffset;
    }

    if (almostAtEdge && shouldLoadMore.value) {
      if (direction === ScrollDirection.TOP) {
        // Save the scroll state before loading
        oldScrollHeight = target.scrollHeight;
        oldScrollTop = target.scrollTop;
        isLoadingMore = true;

        // Call the loadMoreHook (supports async functions)
        const result = loadMoreHook({ scroll: true });
        if (result && typeof result.then === "function") {
          await result;
        }
      } else {
        // For bottom scrolling, simply trigger loadMoreHook
        loadMoreHook({ scroll: true });
      }
    }
  }

  watch(scrollableTarget, (newTarget, oldTarget) => {
    if (oldTarget) {
      oldTarget.removeEventListener("scroll", handleScroll);
    }
    if (newTarget) {
      newTarget.addEventListener("scroll", handleScroll);
    }
  }, { immediate: true });

  onBeforeUnmount(() => {
    if (scrollableTarget.value) {
      scrollableTarget.value.removeEventListener("scroll", handleScroll);
    }
  });

  return {
    shouldLoadMore,
  };
}
